import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/lab/Alert";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { Question } from "../../../models/Question";
import { useFormsContext } from "../../../context/FormsContext";
import { upsertQuestion } from "../../../repositories/FormsRepository";
import { INITIAL_VALUES_QUESTION } from "../../../models/QuestionInitialValue";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	setShouldUpdateWhenExit: (shouldUpdate: boolean) => void;
	data: Question | null;
	newMode?: boolean;
};

export const NewAndEditQuestionModal = ({
	show,
	onHide,
	data,
	newMode = false,
	setShouldUpdateWhenExit,
}: Props) => {
	const {
		state: { SectionId, questionSelectedInTable, QuestionId, QuestionText },
		actions: {
			setQuestionTextInTable,
			setQuestionIncludesCommentInTable,
			setQuestionIsRequiredInTable,
		},
	} = useFormsContext();

	const [editedSuccess, setEditedSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const [error, setError] = useState<string>("");
	const [deleteByIdError, setDeleteByIdError] = useState<string>("");
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
		reset,
	} = useForm<Question>({ defaultValues: INITIAL_VALUES_QUESTION });

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setEditedSuccess(false);
	};
	const handleCancel = async () => {
		reset();
		onHide(false);
	};

	const questionText = watch("QuestionText");

	const changeQuestionText = () => {
		setQuestionTextInTable(questionText);
	};

	const isRequired = watch("IsRequired");

	const changeIsRequired = () => {
		setQuestionIsRequiredInTable(isRequired);
	};
	const includesComment = watch("IncludesComment");

	const changeIncludesComment = () => {
		setQuestionIncludesCommentInTable(includesComment);
	};

	const onSubmit = async (questionData: Question) => {
		setIsSubmitLoading(true);
		const res = await upsertQuestion(
			QuestionId,
			SectionId,
			questionData.QuestionTypeCode,
			questionData.QuestionText,
			questionData.IncludesComment,
			questionData.IsRequired,
		);
		if (res.ok) {
			reset();
			setIsSubmitLoading(false);
			setEditedSuccess(true);
			setShouldUpdateWhenExit(true);
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	useEffect(() => {
		if (data != null && show) {
			reset(INITIAL_VALUES_QUESTION);
			setQuestionTextInTable(data.QuestionText);
			setQuestionIsRequiredInTable(data.IsRequired);
			setQuestionIncludesCommentInTable(data.IncludesComment);
			setValue("QuestionText", data.QuestionText);
			setValue("IsRequired", data.IsRequired);
			setValue("IncludesComment", data.IncludesComment);
			setIsSubmitLoading(false);
		} else {
			reset(INITIAL_VALUES_QUESTION);
		}
	}, [show]);

	useEffect(() => {
		changeQuestionText();
	}, [questionText]);

	useEffect(() => {
		changeIsRequired();
	}, [isRequired]);

	useEffect(() => {
		changeIncludesComment();
	}, [includesComment]);

	return (
		<>
			<MesfModal
				title={newMode ? "NEW QUESTION" : "EDIT QUESTION"}
				open={show}
				handleClose={() => handleCancel()}
				maxWidth="sm"
				id="form-question-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid2 container spacing={2}>
							<Grid2 size={12}>
								<Controller
									name="QuestionText"
									control={control}
									rules={{
										validate: (value) => value !== "",
									}}
									render={({ field: { onChange, value } }) => (
										<TextField
											label="Question Name"
											variant="outlined"
											fullWidth
											error={!!errors.QuestionText}
											margin="dense"
											autoComplete="off"
											value={value ?? ""}
											onChange={(e) => onChange(e)}
											slotProps={{ inputLabel: { shrink: true } }}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="IsRequired"
									control={control}
									defaultValue={questionSelectedInTable?.IsRequired}
									render={({ field }) => (
										<FormControlLabel
											sx={{ userSelect: "none" }}
											control={
												<Checkbox
													checked={field.value as boolean}
													onChange={(e) => field.onChange(e.target.checked)}
													name="IsRequired"
													color="primary"
												/>
											}
											label="Required"
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="IncludesComment"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											sx={{ userSelect: "none" }}
											control={
												<Checkbox
													checked={field.value as boolean}
													onChange={(e) => field.onChange(e.target.checked)}
													name="IncludesComment"
													color="primary"
												/>
											}
											label="Includes Comment"
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 16px 16px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={handleCancel}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				open={editedSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={deleteByIdError}
				onHide={() => setDeleteByIdError("")}
			/>
		</>
	);
};
